import { Table, Title } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import queryString from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Pager from '../../components/Pager/Pager';
import ProfileRows from '../../components/Profiles/ProfileRows';
import Spinner from '../../components/Spinner';
import PageSizeOptions from '../../constants/pageSizeOptions';
import useFetch from '../../helpers/useFetch';

const fetchDebounced = AwesomeDebouncePromise(
  (fetch, accountId, domainId, profileFilter, trackingId) =>
    fetch(
      `/api/admin/accounts/${accountId}/domains/${domainId}/companies/${encodeURIComponent(
        trackingId
      )}/profiles?${queryString.stringify(profileFilter, { arrayFormat: 'bracket' })}`
    ),
  300
);

const filterDefaultValues = { from: 0, size: PageSizeOptions[0] };

const CompaniesDetailsProfilesPage = ({ trackingId }) => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();

  const parsedSearch = useMemo(() => {
    return queryString.parse(location.search, { arrayFormat: 'bracket' });
  }, [location.search]);

  const [profiles, setProfiles] = useState();
  const [profileFilter, setProfileFilter] = useState(filterDefaultValues);
  const [totalCount, setTotalCount] = useState();
  const [pageCount, setPageCount] = useState();
  const [error, setError] = useState();
  const { accountId, domainId } = useParams();

  useEffect(() => {
    const newFilter = { ...filterDefaultValues, ...parsedSearch };
    setProfileFilter(newFilter);
    setProfiles(undefined);
    fetchDebounced(fetch, accountId, domainId, parsedSearch, trackingId)
      .then(({ profiles, totalCount }) => {
        setTotalCount(totalCount);
        setPageCount(Math.ceil(totalCount / profileFilter.size));
        setProfiles(profiles);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, trackingId, parsedSearch, profileFilter.size]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const handlePageClick = (e) => {
    const selectedPage = e.selected;
    const newOffset = (selectedPage - 1) * profileFilter.size;
    if (newOffset !== parsedSearch.from) {
      navigate({
        search: queryString.stringify(
          { ...parsedSearch, from: (selectedPage - 1) * parseInt(profileFilter.size) },
          { arrayFormat: 'bracket' }
        ),
      });
    }
  };

  const setPageSize = async (pageSize) => {
    navigate({ search: queryString.stringify({ ...parsedSearch, size: pageSize }) }, { arrayFormat: 'bracket' });
  };

  if (!profiles) {
    return <Spinner />;
  }

  return (
    <div data-tour="companyDetailsProfiles">
      <Title order={5} mb="md" ta="left">
        <IconUser size={16} /> {totalCount.toLocaleString(navigator.language)} Profiles
      </Title>
      {profiles && (
        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Profile</Table.Th>
              <Table.Th>Email</Table.Th>
              <Table.Th>Phone number</Table.Th>
              <Table.Th>Registered At</Table.Th>
              <Table.Th>Subscribed At</Table.Th>
              <Table.Th>Last Change At</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <ProfileRows profiles={profiles} />
          </Table.Tbody>
        </Table>
      )}
      <Pager
        pageSize={parseInt(profileFilter.size)}
        pageCount={pageCount}
        onPageChange={handlePageClick.bind(this)}
        initialPage={Math.round(parseInt(parsedSearch.from || '0') / parseInt(profileFilter.size) + 1)}
        setPageSize={setPageSize}
      />
    </div>
  );
};

export default CompaniesDetailsProfilesPage;
